import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, XCircle } from "lucide-react";

export default function OrdersManager() {
  const [orders, setOrders] = useState([]);
  const [file, setFile] = useState(null);
  const [filter, setFilter] = useState(""); // "", "matched", "unmatched"
  const [uploadResult, setUploadResult] = useState(null);

  const load = async () => {
    try {
      const params = filter ? { match_status: filter } : {};
      const { data } = await api.get("/admin/orders", { params });
      setOrders(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [filter]);

  const upload = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/orders/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(data);
      toast.success(`${data.matched}/${data.total} ordini con match`);
      setFile(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="orders-manager">
      <div>
        <h2 className="mgx-display text-4xl text-[#003D5C]">Ordini & Match coupon</h2>
        <p className="text-sm text-[#6B7280] mt-2">Carica un file Excel/CSV con gli ordini. Il sistema farà automaticamente il match con i coupon inviati.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1 mgx-card">
          <div className="mgx-label">Carica ordini</div>
          <p className="text-xs text-[#6B7280] mt-2 leading-relaxed">
            Colonne richieste: <span className="font-mono">riferimento, prodotto, quantità, valore, codice coupon</span>
            <br /> (oppure in inglese: customer, product, quantity, value, coupon)
          </p>
          <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} className="mt-4 text-sm" data-testid="orders-file-input" />
          <div className="text-xs text-[#6B7280] mt-2 mb-4">{file ? file.name : ""}</div>
          <button onClick={upload} disabled={!file} className="mgx-btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="orders-upload-btn">
            <Upload size={14} /> Carica e fai il match
          </button>
        </div>

        <div className="md:col-span-2">
          {uploadResult && (
            <div className="mgx-card mb-4 border-[#E30613]/40 bg-[#E30613]/5">
              <div className="mgx-label">Ultimo caricamento</div>
              <div className="flex gap-6 mt-2">
                <div><span className="mgx-display text-3xl text-[#003D5C]">{uploadResult.total}</span><div className="text-xs text-[#6B7280]">Totale</div></div>
                <div><span className="mgx-display text-3xl text-[#2E6F40]">{uploadResult.matched}</span><div className="text-xs text-[#6B7280]">Match</div></div>
                <div><span className="mgx-display text-3xl text-[#E30613]">{uploadResult.unmatched}</span><div className="text-xs text-[#6B7280]">No match</div></div>
              </div>
            </div>
          )}

          <div className="mgx-card">
            <div className="flex items-center justify-between mb-4">
              <div className="mgx-label">Storico ordini</div>
              <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-sm border border-[#E5E7EB] px-3 py-2 bg-white" data-testid="orders-filter">
                <option value="">Tutti</option>
                <option value="matched">Solo match</option>
                <option value="unmatched">Solo no-match</option>
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="orders-table">
                <thead>
                  <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.15em] text-[#6B7280]">
                    <th className="py-3 pr-4">Cliente</th>
                    <th className="py-3 pr-4">Prodotto</th>
                    <th className="py-3 pr-4 text-right">Valore</th>
                    <th className="py-3 pr-4">Coupon</th>
                    <th className="py-3 pr-4">Hotel</th>
                    <th className="py-3 pr-4">Stato</th>
                    <th className="py-3 pr-4 text-right">Commissione</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 && (
                    <tr><td colSpan={7} className="py-10 text-center text-[#6B7280]">Nessun ordine caricato</td></tr>
                  )}
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-[#E5E7EB]/50">
                      <td className="py-3 pr-4">{o.customer}</td>
                      <td className="py-3 pr-4 text-[#6B7280]">{o.product}</td>
                      <td className="py-3 pr-4 text-right">€ {o.value}</td>
                      <td className="py-3 pr-4 font-mono text-xs">{o.coupon_code || "—"}</td>
                      <td className="py-3 pr-4 text-[#6B7280]">{o.hotel_name || "—"}</td>
                      <td className="py-3 pr-4">
                        {o.match_status === "matched" ? (
                          <span className="inline-flex items-center gap-1 text-[#2E6F40] text-xs"><CheckCircle2 size={14} /> Match</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#E30613] text-xs"><XCircle size={14} /> No match</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-right text-[#E30613] mgx-display text-lg">€ {o.commission_amount || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
